import { render } from "preact";
import IndoorPanel from "./indoor-panel";
import NavigationWidget from "sdk/navigation/navigation-control";

/**
 * UI Option of the Indoor widget.
 * @public
 * @memberOf woosmap.map
 */
export type IndoorWidgetOptionsui = {
  primaryColor: string;
  secondaryColor: string;
};
/**
 * Widget's autocomplete options
 * @public
 * @memberOf woosmap.map
 */
export type IndoorWidgetAutocompleteOptions = {
  /**
   * Number of search responses to retrieve. Default is 5 and the maximum limit is 10.
   */
  maxResponses: number;
  /**
   * If set true, search responses will be retrived with distance from the user's location
   */
  autocompleteWithDistance: boolean;
  /**
   *  Filter the map and the autocomplete to display/search only labels and icons of POIs which are matching the filters
   */
  baseFilter: string;
  /**
   * Sets whether to disable the autofocus for the search input.
   */
  disableAutoFocus?: boolean;
};

/**
 * Options of the Indoor widget.
 * @public
 * @memberOf woosmap.map
 */
export type IndoorWidgetOptions = {
  /**
   * Set the custom colors of the indoor widget
   */
  ui?: IndoorWidgetOptionsui;
  /**
   * Set units to use to show distance in directions itinerary
   */
  units?: "imperial" | "metric" | "";
  /**
   * @deprecated Use baseFilter of autocomplete option instead
   *  Filter the map and the autocomplete to display/search only labels and icons of POIs which are matching the filters
   */
  baseFilter?: string;
  /**
   * Set directions mode/routing profile
   */
  directionsMode?: string;
  /**
   * @deprecated Use autocompleteWithDistance of autocomplete option instead
   * If set true, search responses will be retrived with distance from the user's location
   */
  autocompleteWithDistance?: boolean;
  /**
   * Sets whether to enable/disable navigation mode.
   */
  navigationMode?: boolean;
  /**
   * Areas to avoid while routing. Useful when certain areas of the venues are restricted or under maintenance.
   * Expected format `level;lat,lng;lat,lng;lat,lng|level;lat,lng;lat,lng;lat,lng`
   */
  avoidZones?: string;
  /**
   * A set of optional autocomplete options to enrich the autocomplete backend request.
   */
  autocomplete?: IndoorWidgetAutocompleteOptions;
  /**
   * Custom property exclusive for SDM
   */
  widgetMapMode?: "general" | "favorites" | "thematic" | "";
};
/**
 * @memberOf woosmap.map
 */
export default class IndoorWidget {
  _ui:
    | {
      primaryColor: string;
      secondaryColor: string;
    }
    | null
    | undefined;
  _units: "imperial" | "metric";
  _directionsMode: string | null | undefined;
  _navigationMode: boolean;
  _renderer: woosmap.map.IndoorRenderer;
  _container: HTMLElement | null | undefined;
  _navigationController: any;
  _avoidZones: string;
  _baseFilter: string | null;
  _autocompleteWithDistance: boolean;
  _maxResponses: number;
  _disableAutoFocus: boolean;
  _widgetMapMode: "general" | "favorites" | "thematic" | "kiosk";
  _selectedWaypoints: woosmap.map.GeoJSONFeature[] | null | undefined;
  _selectedDestination: woosmap.map.GeoJSONFeature | null | undefined;
  _isItineraryMode: boolean;

  /**
   * Creates a new Indoor widget.
   * @param [widgetOptions] IndoorWidgetOptions
   * @param [rendererOptions] IndoorRendererOptions
   * @constructor
   */
  constructor(
    widgetOptions?: IndoorWidgetOptions,
    rendererOptions?: woosmap.map.IndoorRendererOptions,
  ) {
    if (!widgetOptions) {
      widgetOptions = {};
    }
    this._ui = widgetOptions.ui || null;
    this._units = widgetOptions.units || "metric";
    this._directionsMode = widgetOptions.directionsMode || null;
    this._navigationMode =
      widgetOptions.navigationMode !== undefined
        ? widgetOptions.navigationMode
        : true;
    this._avoidZones = widgetOptions.avoidZones || "";
    this._widgetMapMode = widgetOptions.widgetMapMode || "general";
    this._isItineraryMode = false;

    if (
      widgetOptions.autocomplete &&
      widgetOptions.autocomplete.disableAutoFocus !== undefined
    ) {
      this._disableAutoFocus = widgetOptions.autocomplete.disableAutoFocus;
    } else {
      this._disableAutoFocus = false;
    }
    if (widgetOptions.baseFilter) {
      this._baseFilter = widgetOptions.baseFilter;
    } else {
      this._baseFilter =
        (widgetOptions.autocomplete &&
          widgetOptions.autocomplete.baseFilter &&
          widgetOptions.autocomplete.baseFilter) ||
        null;
    }
    if (widgetOptions.autocompleteWithDistance !== undefined) {
      this._autocompleteWithDistance = widgetOptions.autocompleteWithDistance;
    } else {
      this._autocompleteWithDistance =
        widgetOptions.autocomplete &&
          widgetOptions.autocomplete.autocompleteWithDistance !== undefined
          ? widgetOptions.autocomplete.autocompleteWithDistance
          : false;
    }

    this._maxResponses =
      widgetOptions.autocomplete &&
        widgetOptions.autocomplete.maxResponses !== undefined
        ? widgetOptions.autocomplete.maxResponses
        : 5;

    this._renderer = new window.woosmap.map.IndoorRenderer(rendererOptions);
    this._renderer.addListener("indoor_venue_loaded", (data) => {
      this._renderPanel();
      this.trigger(this, "indoor_venue_loaded", [data]);
    });
    this._renderer.addListener("indoor_feature_selected", (data) => {
      this.trigger(this, "indoor_feature_selected", [data]);
    });
    this._renderer.addListener("indoor_level_changed", (data) => {
      this.trigger(this, "indoor_level_changed", [data]);
    });
    this._renderer.addListener("indoor_user_location", (data) => {
      this.trigger(this, "indoor_user_location", [data]);
    });
    this._renderer.addListener("indoor_highlight_step", (data) => {
      this.trigger(this, "indoor_highlight_step", [data]);
    });
    this._renderer.addListener("indoor_user_deviated", (data) => {
      this.trigger(this, "indoor_user_deviated", [data]);
    });
    this._renderer.addListener("indoor_user_near_arrival", () => {
      this.trigger(this, "indoor_user_near_arrival", []);
    });
    const root = document.documentElement;
    if (this._ui && root) {
      if (this._ui.primaryColor) {
        root.style.setProperty("--primary-color", this._ui.primaryColor);
      }
      if (this._ui.secondaryColor) {
        root.style.setProperty("--secondary-color", this._ui.secondaryColor);
      }
    }
  }
  /**
   * @private
   */
  onAdd(): HTMLElement {
    const container = (this._container = document.createElement("div"));
    container.classList.add("woosmap-indoor-panel");
    container.classList.add("hide");
    container.classList.add("mapboxgl-ctrl");
    return container;
  }

  /**
   * @private
   */
  onRemove(): void {
    if (this._container && this._container.parentNode) {
      this._container.parentNode.removeChild(this._container);
    }
  }

  /**
   * Sets the map where to render the Indoor widget.
   */
  setMap(map: woosmap.map.Map): void {
    if (map) {
      this._renderer.setMap(map);
      (this._renderer as any)._getMapboxMap(map).addControl(this, "top-left");
    } else {
      this.onRemove();
      this.setDirections(null);
      if (this._navigationController) {
        this.stopNavigation();
      }
      this._renderer.setMap(map);
    }
  }

  /**
   * Renders map with the selected venue
   */
  setVenue(venueId: string): void {
    this._renderer.setVenue(venueId);
  }

  /**
   * Renders a map with a POI highlighted by pk / id
   * silent parameter will prevent the indoor_feature_selected event to trigger
   * padding parameter will apply padding to the map container while highlighting the POI on map
   */
  highlightFeature(
    id: string,
    silent: boolean = false,
    padding?: woosmap.map.Padding,
  ): void {
    this._renderer.highlightFeature(id, silent, padding);
  }
  /**
   * Renders a map with a POI highlighted by its ref
   * padding parameter will apply padding to the map container while highlighting the POI on map
   */
  highlightFeatureByRef(ref: string, padding?: woosmap.map.Padding): void {
    this._renderer.highlightFeatureByRef(ref, padding);
  }
  /**
   * Unselect a selected feature: it closes the widget and unhighlight the feature.
   */
  unselectFeature(): void {
    this._renderer.unselectFeature();
  }
  /**
   * Call this function to draw a polyline on map.
   */
  setDirections(directions: woosmap.map.IndoorDirectionResult | null): void {
    this._renderer.setDirections(directions);
  }

  /**
   * Sets the distance units.
   */
  setUnits(units: "imperial" | "metric"): void {
    this._units = units;
  }

  /**
   * Gets the distance units.
   */
  getUnits(): "imperial" | "metric" {
    return this._units;
  }

  /**
   * Sets the routing profile (or directions mode) ('security' | 'wheelchair') for way finding
   */
  setDirectionsMode(profile: string): void {
    this._directionsMode = profile;
  }

  /**
   * Gets the distance units.
   */
  getDirectionsMode(): string | null | undefined {
    return this._directionsMode;
  }

  /**
   * Sets the baseFilter.
   */
  setBaseFilter(filter: string): void {
    this._baseFilter = filter;
  }

  /**
   * Gets the baseFilter
   */
  getBaseFilter(): string | null {
    return this._baseFilter;
  }

  /**
   * Sets the current user location.
   */
  setUserLocation(
    lat: number,
    lng: number,
    level: number,
    bearing?: number,
    forceFocus?: boolean,
  ): void {
    this._renderer.setUserLocation(lat, lng, level, bearing, forceFocus);
  }
  /**
   * Sets the navigation mode
   */
  setNavigationMode(allowNavigation: boolean): void {
    this._navigationMode = allowNavigation;
  }

  /**
   * Gets the navigates mode
   */
  getNavigationMode(): boolean {
    return this._navigationMode;
  }

  /**
   * Gets the autocompleteWithDistance option
   */
  getAutocompleteWithDistance(): boolean {
    return this._autocompleteWithDistance;
  }
  /**
   * Gets the autocomplete's maxResponses option value if set to determine the number of search suggestions to retrieve. The default value is 5 and the maximum limit is 10.
   */
  getAutocompleteMaxResponses(): number {
    return this._maxResponses || 5;
  }
  /**
   * Renders map with custom theme
   * @param theme Theme name
   */
  setTheme(theme: string): void {
    this._renderer.setTheme(theme);
  }
  /**
   * Sets the zones to avoid during routing. Argument expected in the format level;lat,lng;lat,lng;lat,lng...|level;lat,lng;lat,lng;lat,lng...
   */
  setZonesToAvoid(avoidZones: string) {
    this._avoidZones = avoidZones;
  }
  /**
   * Gets the zones to avoid while routing
   */
  getZonesToAvoid(): string {
    return this._avoidZones;
  }
  /**
   * Sets widget's map mode. This is exclusive for SDM
   */
  setWidgetMapMode(
    mode: "general" | "favorites" | "thematic" | "kiosk",
    favoritesShops?: woosmap.map.GeoJSONFeatureCollection,
    isUnauthorized?: boolean,
  ) {
    this._widgetMapMode = mode;
    this.trigger(this, "indoor_widget_map_changed", [mode, favoritesShops, isUnauthorized]);
  }
  /**
   * Gets the widget map mode. This is exclusive for SDM
   */
  getWidgetMapMode(): "general" | "favorites" | "thematic" | "kiosk" {
    return this._widgetMapMode;
  }
  /**
   * Sets whether to disable the autofocus for the search input.
   */
  setIsAutofocusDisabled(disableFocus: boolean) {
    this._disableAutoFocus = disableFocus;
  }
  /**
   * Gets whether auto focus on search input is enabled/disabled
   */
  getIsAutofocusDisabled(): boolean {
    return this._disableAutoFocus;
  }
  launchNavigation(): void {
    if (!this._navigationController) {
      this._navigationController = new NavigationWidget(
        this._renderer,
        () => {
          this.stopNavigation();
        },
        this._selectedWaypoints,
        this.getSelectedDestination()
      );
      this._navigationController.setMap((this._renderer as any).map);
    }
  }

  stopNavigation(): void {
    this._navigationController.setMap(null);
    this._navigationController = null;
    this.trigger(this, "indoor_stop_navigation", []);
  }

  /**
   * Sets the floor for the venue
   */
  setFloor(floor: number): void {
    this._renderer.setFloor(floor);
  }
  /**
   * Gets the underlying instance of Indoor Renderer when using the Indoor Widget
   */
  getRenderer(): woosmap.map.IndoorRenderer {
    return this._renderer;
  }
  setSelectedWaypoints(selectedWaypoints: woosmap.map.GeoJSONFeature[] | null) {
    this._selectedWaypoints = selectedWaypoints;
  }
  getSelectedWaypoints(): woosmap.map.GeoJSONFeature[] | null | undefined {
    return this._selectedWaypoints;
  }
  setSelectedDestination(destination: woosmap.map.GeoJSONFeature | null | undefined) {
    this._selectedDestination = destination;
  }
  getSelectedDestination(): woosmap.map.GeoJSONFeature | null | undefined {
    return this._selectedDestination;
  }
  setIsItineraryMode(flag: boolean) {
    this._isItineraryMode = flag;
  }
  isItineraryMode(): boolean {
    return this._isItineraryMode;
  }
  /**
   * @private
   */
  _renderPanel(): void {
    if (this._container) {
      render(<IndoorPanel controller={this} />, this._container);
    }
  }

  /**
   * Adds a listener for eventName.
   */
  addListener(
    eventName: string,
    handler: (...args: any[]) => any,
  ): woosmap.map.MapEventListener {
    return woosmap.map.event.addListener(this, eventName, handler);
  }
  /**
   *  Triggers the given event. All arguments after eventName are passed as arguments to the listeners.
   */
  trigger(instance: Object, eventName: string, eventArgs: Array<any>): void {
    return woosmap.map.event.trigger(this, eventName, eventArgs);
  }
  /**
   * Opens the widget's panel in itinerary mode.
   * @param origin Origin can be any of the following, A GeoJSON feature or string of coordinates in the format(lat,lng,level) or user's current location
   * @param destination Destination can be any of the following, A GeoJSON feature or string of coordinates in the format(lat,lng,level) or user's current location
   * @param source Optional analytics context indicating how the itinerary was triggered (e.g. 'frame', 'main_map')
   */
  showItinerary(
    origin?: woosmap.map.GeoJSONFeature | Object | string,
    destination?: woosmap.map.GeoJSONFeature | Object | string,
    source?: string,
  ) {
    this.trigger(this, "widget_open_itinerary_panel", [origin, destination, source]);
  }
}
